import {
  faCalendarCheck,
  faCalendarPlus,
  faFloppyDisk,
  faLink,
  faLinkSlash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useToasts } from 'react-toast-notifications';
import { createDowntime, linkDowntime, unlinkDowntime } from 'src/api/maintenance';
import Alert from 'src/components/ui/Alert';
import Button from 'src/components/ui/Button';
import Input from 'src/components/ui/Input';
import SelectableCard from 'src/components/ui/SelectableCard';
import Skeleton from 'src/components/ui/Skeleton';
import { useLinkableDowntimes } from 'src/hooks/services/useMaintenance';
import {
  buildDefaultDowntimeForm,
  serializeDowntimeForm,
  validateDowntimeForm,
} from 'src/utils/maintenance';
import { getUuid } from 'src/utils/url';

import { DowntimePickRow } from '../DowntimeCards';

const CHOICE = { KEEP: 'keep', SWITCH: 'switch', CREATE: 'create', UNLINK: 'unlink' };

const Field = ({ label, children }) => (
  <div className='mnt-field'>
    <span className='mnt-field__label'>{label}</span>
    {children}
  </div>
);

Field.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node.isRequired };

const LinkableDowntimeList = ({ loading, linkable, selectedUri, onSelect }) => {
  if (loading) {
    return (
      <>
        <Skeleton width='60%' height={13} style={{ marginBottom: 10 }} />
        <Skeleton width='45%' height={13} />
      </>
    );
  }
  if (linkable.length === 0) {
    return (
      <p className='text-muted mb-0' style={{ fontSize: 12.5 }}>
        No other unlinked windows on this machine.
      </p>
    );
  }
  return linkable.map(downtime => (
    <DowntimePickRow
      key={downtime.uri}
      downtime={downtime}
      selected={selectedUri === downtime.uri}
      onSelect={picked => onSelect(picked.uri)}
    />
  ));
};

LinkableDowntimeList.propTypes = {
  loading: PropTypes.bool,
  linkable: PropTypes.arrayOf(PropTypes.object),
  selectedUri: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

LinkableDowntimeList.defaultProps = {
  loading: false,
  linkable: [],
  selectedUri: null,
};

/**
 * Change-downtime sheet (planned events only). Link, switch, create, or unlink
 * the window. Switching/creating a replacement unlinks the current one first
 * (the backend forbids re-linking to a different downtime directly).
 */
const ChangeDowntimeSheet = ({
  anchor,
  event,
  instanceTargetUri,
  linkedDowntime,
  assignedUserUri,
  onDone,
}) => {
  const { addToast } = useToasts();
  const [choice, setChoice] = useState(linkedDowntime ? CHOICE.KEEP : CHOICE.SWITCH);
  const [selectedUri, setSelectedUri] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() =>
    buildDefaultDowntimeForm(`Maintenance - ${anchor.instanceLabel}`)
  );

  const { data: linkable = [], isLoading: linkableLoading } = useLinkableDowntimes(
    anchor.downtimeWorkstationField,
    instanceTargetUri
  );

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const eventUuid = getUuid(event.uri);
  const downtimeError = choice === CHOICE.CREATE ? validateDowntimeForm(form) : null;

  const handleSave = async () => {
    if (choice === CHOICE.KEEP) {
      onDone();
      return;
    }
    setSaving(true);
    try {
      if (choice === CHOICE.UNLINK) {
        await unlinkDowntime(eventUuid, addToast);
      } else {
        // Bail before any mutation if the create form is invalid, otherwise the
        // unlink below could run and strand the event with no downtime.
        if (choice === CHOICE.CREATE && downtimeError) return;
        if (linkedDowntime) await unlinkDowntime(eventUuid, addToast);
        let downtimeUri = selectedUri;
        if (choice === CHOICE.CREATE) {
          const created = await createDowntime(
            {
              ...serializeDowntimeForm(form),
              assignedUser: assignedUserUri,
              workstationField: anchor.downtimeWorkstationField,
              workstationUri: instanceTargetUri,
            },
            addToast
          );
          downtimeUri = created.uri;
        }
        if (downtimeUri) await linkDowntime(eventUuid, downtimeUri, addToast);
      }
      addToast('Downtime updated.', { appearance: 'success' });
      onDone();
    } catch (error) {
      console.error(error);
      if (!error?.response) {
        addToast('Could not update the downtime. Please try again.', { appearance: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const saveDisabled =
    saving || Boolean(downtimeError) || (choice === CHOICE.SWITCH && !selectedUri);

  return (
    <div className='mnt-sheet'>
      <p className='text-muted' style={{ fontSize: 12.5 }}>
        Link, switch, or {linkedDowntime ? 'unlink' : 'attach'} the downtime window for this event.
        {!linkedDowntime && ' Unlinking is only allowed while the event is still planned.'}
      </p>

      {linkedDowntime && (
        <SelectableCard
          id={CHOICE.KEEP}
          name='mnt-change-dt'
          icon={faLink}
          title='Keep current window'
          active={choice === CHOICE.KEEP}
          onSelect={setChoice}
        >
          <DowntimePickRow readOnly downtime={linkedDowntime} />
        </SelectableCard>
      )}

      <SelectableCard
        id={CHOICE.SWITCH}
        name='mnt-change-dt'
        icon={faCalendarCheck}
        title='Use a different window'
        active={choice === CHOICE.SWITCH}
        onSelect={setChoice}
      >
        <LinkableDowntimeList
          loading={linkableLoading}
          linkable={linkable}
          selectedUri={selectedUri}
          onSelect={setSelectedUri}
        />
      </SelectableCard>

      <SelectableCard
        id={CHOICE.CREATE}
        name='mnt-change-dt'
        icon={faCalendarPlus}
        title='Create a new window'
        active={choice === CHOICE.CREATE}
        onSelect={setChoice}
      >
        <Field label='Description'>
          <Input value={form.description} onChange={e => setField('description', e.target.value)} />
        </Field>
        <Field label='Start'>
          <div className='mnt-field__row'>
            <Input
              type='date'
              value={form.startDate}
              onChange={e => setField('startDate', e.target.value)}
            />
            <Input
              type='time'
              value={form.startTime}
              onChange={e => setField('startTime', e.target.value)}
            />
          </div>
        </Field>
        <Field label='Finish'>
          <div className='mnt-field__row'>
            <Input
              type='date'
              value={form.finishDate}
              onChange={e => setField('finishDate', e.target.value)}
            />
            <Input
              type='time'
              value={form.finishTime}
              onChange={e => setField('finishTime', e.target.value)}
            />
          </div>
        </Field>
        {downtimeError && (
          <Alert variant='danger' className='mt-2 mb-0'>
            {downtimeError}
          </Alert>
        )}
      </SelectableCard>

      {linkedDowntime && (
        <SelectableCard
          danger
          id={CHOICE.UNLINK}
          name='mnt-change-dt'
          icon={faLinkSlash}
          title='Unlink downtime'
          desc='Remove the link. The downtime row is kept for audit.'
          active={choice === CHOICE.UNLINK}
          onSelect={setChoice}
        />
      )}

      <Button
        fill
        block
        variant='primary'
        size='large'
        className='mt-2'
        disabled={saveDisabled}
        loading={saving}
        icon={<FontAwesomeIcon icon={faFloppyDisk} />}
        onClick={handleSave}
      >
        {saving ? 'Saving…' : 'Save changes'}
      </Button>
    </div>
  );
};

ChangeDowntimeSheet.propTypes = {
  anchor: PropTypes.object.isRequired,
  event: PropTypes.object.isRequired,
  instanceTargetUri: PropTypes.string.isRequired,
  linkedDowntime: PropTypes.object,
  assignedUserUri: PropTypes.string,
  onDone: PropTypes.func.isRequired,
};

ChangeDowntimeSheet.defaultProps = { linkedDowntime: null, assignedUserUri: null };

export default ChangeDowntimeSheet;
